// The drive screen (docs/spec/transport.md slice 7): a full-screen map that follows the bus
// heading-up, the next turn read out, and at each stop the children to pick up or drop with a tap.
// Directions come from our own routing server through the worker; the phone asks again when the
// bus leaves the line or the next stop changes. Every tap is the same signed event as in the list.
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Banner, Button, Icon } from "@vavr/ui";
import { api, ApiError, fullName, MapView, type MapMarker } from "@vavr/screens/shared";
import { useTeacher } from "@vavr/screens/teacher";
import { phoneClock } from "./status.tsx";
import { StopLine } from "./stopline.tsx";
import { actionAt, bearing, cumulative, lineBearing, distanceLabel, dueAt, guidance, nextStop, snap, type Action, type Directions, type DriveRider, type DriveRoute, type DriveStop } from "./drive.ts";

export interface DriveRiderView extends DriveRider { site_id: string; first: string; last: string }
interface Fix { lat: number; lng: number; accuracy: number; speed: number | null; heading: number | null; at: number }

const MUTE_KEY = "pmu.drive.muted";
const readMuted = () => { try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; } };

/** Valhalla's maneuver types as an arrow's turn, in degrees (null: arriving). */
function turnOf(type: number): number | null {
  if (type >= 4 && type <= 6) return null;
  return ({ 9: 45, 10: 90, 11: 135, 12: 180, 13: -180, 14: -135, 15: -90, 16: -45, 18: 45, 19: -45, 20: 45, 21: -45, 23: 20, 24: -20 } as Record<number, number>)[type] ?? 0;
}

function TurnArrow({ type, small }: { type: number; small?: boolean }) {
  const turn = turnOf(type);
  const cls = `pmt-drive__turn${small ? " pmt-drive__turn--small" : ""}`;
  if (turn === null) return <span className={cls} aria-hidden="true"><Icon name="arrive" /></span>;
  return (
    <span className={cls} aria-hidden="true">
      {/* Drawn on the box's centre (24, 24), stroke included, and turned about it: centred at every angle. */}
      <svg viewBox="0 0 48 48" width={small ? 20 : 36} height={small ? 20 : 36}><g transform={`rotate(${turn} 24 24)`}><path d="M24 40V11" stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" /><path d="M13 19L24 8l11 11" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" /></g></svg>
    </span>
  );
}

/** The phone's GPS while the drive screen is open, with a heading worked out when the phone gives none. */
function usePosition() {
  const [fix, setFix] = useState<Fix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const last = useRef<Fix | null>(null);
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) { setError("This phone has no location."); return; }
    const id = navigator.geolocation.watchPosition((p) => {
      const c = p.coords;
      const prev = last.current;
      let heading = c.heading != null && !Number.isNaN(c.heading) && (c.speed ?? 0) > 1.5 ? c.heading : null;
      if (heading == null && prev) {
        const moved = Math.hypot((c.latitude - prev.lat) * 111_320, (c.longitude - prev.lng) * 111_320 * Math.cos((c.latitude * Math.PI) / 180));
        heading = moved > 8 ? bearing(prev, { lat: c.latitude, lng: c.longitude }) : prev.heading;
      }
      const f = { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, speed: c.speed, heading, at: p.timestamp };
      if (!prev || heading !== prev.heading || Math.abs(f.lat - prev.lat) + Math.abs(f.lng - prev.lng) > 2e-6) last.current = f;
      setFix(f); setError(null);
    }, (e) => setError(e.code === e.PERMISSION_DENIED ? "Allow Vavr Staff to use this phone's location." : "Waiting for GPS."), { enableHighAccuracy: true, maximumAge: 2000, timeout: 20_000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);
  return { fix, error };
}

/**
 * The screen stays on while driving: natively in the app (the WebView's Wake Lock reports success
 * but the screen still times out), else the Screen Wake Lock, taken again when the page comes back.
 */
function useWakeLock(drive?: { keepScreenOn(on: boolean): Promise<void> }) {
  useEffect(() => {
    if (drive) { void drive.keepScreenOn(true); return () => { void drive.keepScreenOn(false); }; }
    let lock: { release(): Promise<void> } | null = null;
    const take = () => { (navigator as Navigator & { wakeLock?: { request(t: "screen"): Promise<{ release(): Promise<void> }> } }).wakeLock?.request("screen").then((l) => { lock = l; }, () => undefined); };
    take();
    const onVis = () => { if (document.visibilityState === "visible") take(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); void lock?.release(); };
  }, [drive]);
}

export function DriveView<R extends DriveRiderView>({ route, runId, riders, onAct, onHandTo, onList, onFinish }: {
  route: DriveRoute & { name: string; stops: (DriveStop & { lat: number | null; lng: number | null })[] };
  runId: string;
  riders: R[];
  onAct: (x: R, action: Action | "absent", stop: DriveStop) => void;
  onHandTo: (x: R) => void;
  onList: () => void;
  onFinish: () => void;
}) {
  const { site, platform } = useTeacher();
  const { fix, error: gpsError } = usePosition();
  useWakeLock(platform.drive);
  const next = nextStop(route, riders);
  const [dir, setDir] = useState<Directions | null>(null);
  const [dirError, setDirError] = useState<string | null>(null);
  const [atStop, setAtStop] = useState(false);
  const [muted, setMuted] = useState(readMuted);
  const asked = useRef<{ stop: string | null; at: number; off: number }>({ stop: null, at: 0, off: 0 });
  const spoken = useRef(new Set<string>());
  const cum = useMemo(() => (dir ? cumulative(dir.shape) : []), [dir]);
  const g = dir && fix ? guidance(dir, fix, cum) : null;

  const say = (key: string, text: string) => {
    if (muted || spoken.current.has(key)) return;
    spoken.current.add(key);
    if (platform.drive) void platform.drive.speak(text);
    else if (typeof speechSynthesis !== "undefined") { speechSynthesis.cancel(); speechSynthesis.speak(new SpeechSynthesisUtterance(text)); }
  };

  // Directions: for a new next stop, and again when the bus has left the line (twice in a row, at most every 15 s).
  useEffect(() => {
    if (!fix || !next) return;
    const place = next.lat != null && next.lng != null;
    const stopChanged = asked.current.stop !== next.id;
    if (g?.offRoute) asked.current.off++; else asked.current.off = 0;
    const since = Date.now() - asked.current.at;
    // Leaving the line (twice in a row), or no directions yet after a failure: ask again, at most every 15 s.
    const reroute = !stopChanged && since > 15_000 && (asked.current.off >= 2 || (!dir && place));
    if (!stopChanged && !reroute) return;
    asked.current = { stop: next.id, at: Date.now(), off: 0 };
    const wasOff = !!dir;
    if (stopChanged) { setDir(null); setAtStop(false); spoken.current.clear(); }
    if (!place) { setDirError(`${next.name} has no place on the map. Ask your dispatcher to place it.`); return; }
    api<Directions>("POST", `/api/v1/sites/${site.id}/transport/runs/${runId}/directions`, { from: { lat: fix.lat, lng: fix.lng, heading: fix.heading ?? undefined }, stop_id: next.id })
      .then((d) => { setDir(d); setDirError(null); if (reroute && wasOff) say(`reroute-${Date.now()}`, "Rerouting."); },
        (e: ApiError) => setDirError(e.message));
  }, [fix?.lat, fix?.lng, next?.id]);

  // Spoken prompts: once early (about a quarter mile out), once at the turn; and on arrival.
  useEffect(() => {
    if (!g || !dir || !next) return;
    if (g.arrived && !atStop) {
      setAtStop(true);
      const n = dueAt(route, next, riders).length;
      say(`arrive-${next.id}`, `Arriving at ${next.name}. ${n === 1 ? "One child." : `${n} children.`}`);
      return;
    }
    const m = g.next;
    if (!m || turnOf(m.type) === null) return;
    const key = `${dir.stop.id}-${m.begin}`;
    if (g.toNextM <= 70) say(`${key}-now`, m.pre);
    else if (g.toNextM <= 420) say(`${key}-soon`, `In ${distanceLabel(g.toNextM).replace(" mi", " miles").replace(" ft", " feet")}, ${m.alert ?? m.instruction}`);
  }, [g?.toNextM, g?.arrived, g?.next?.begin]);

  const toggleMute = () => { const v = !muted; setMuted(v); try { localStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch { /* per phone, best effort */ } if (v) void platform.drive?.stop(); };

  // Following the bus heading-up, until the driver moves the map by hand; the overview shows the whole way left.
  const [follow, setFollow] = useState(true);
  const [overview, setOverview] = useState(false);
  const [seq, setSeq] = useState(0);
  const recentre = () => { setOverview(false); setFollow(true); setSeq((n) => n + 1); };

  // The map: the rest of the line, the stops (done greyed, the next one marigold), and the bus's arrow.
  const me = g?.onLine ?? (fix ? { lat: fix.lat, lng: fix.lng } : null);
  // Heading-up: the phone's own heading while moving; standing still, the way the route goes from here.
  const heading = fix?.heading ?? (dir && fix ? lineBearing(dir.shape, cum, fix) : 0);
  // The line starts where the arrow was at the last fix: the arrow glides there from it, so the line
  // stays under the arrow instead of starting a few metres ahead of it.
  const behind = useRef<{ lat: number; lng: number } | null>(null);
  const line = useMemo(() => {
    if (dir && fix) {
      const s = snap(dir.shape, fix);
      const was = behind.current, at = g!.onLine;
      behind.current = at;
      const back = was ? snap(dir.shape, was) : null;
      const start = back && back.seg <= s.seg && s.along - back.along < 120 ? [[was!.lng, was!.lat] as [number, number], ...dir.shape.slice(back.seg + 1, s.seg + 1)] : [];
      return [...start, [at.lng, at.lat] as [number, number], ...dir.shape.slice(s.seg + 1)];
    }
    if (fix && next?.lat != null && next.lng != null) return [[fix.lng, fix.lat], [next.lng, next.lat]] as [number, number][];
    return null;
  }, [dir, fix?.lat, fix?.lng, next?.id]);
  const ordered = useMemo(() => [...route.stops].sort((a, b) => a.seq - b.seq), [route.stops]);
  const homeStops = ordered.filter((s) => !s.site_id);
  const nextAt = next ? ordered.findIndex((s) => s.id === next.id) : ordered.length;
  const markers: MapMarker[] = [
    ...ordered.flatMap((s, i) => (s.lat != null && s.lng != null ? [{
      id: `stop-${s.id}`, lat: s.lat, lng: s.lng, kind: s.site_id ? "school" as const : "stop" as const,
      label: s.site_id ? undefined : String(homeStops.indexOf(s) + 1), title: s.name,
      tone: i < nextAt ? "done" as const : i === nextAt ? "next" as const : undefined,
    }] : [])),
    ...(me ? [{ id: "me", lat: me.lat, lng: me.lng, kind: "me" as const, title: "You", heading }] : []),
  ];
  const tall = typeof window !== "undefined" ? window.innerHeight : 800;
  const camera = me && follow && !overview ? { lng: me.lng, lat: me.lat, bearing: heading, zoom: g && g.remainingM < 150 ? 17 : g && g.toNextM < 250 ? 16.2 : 15.2, pitch: 45, padding: { top: Math.round(tall * 0.34), bottom: 150 }, seq } : null;
  const overviewView = overview ? {
    key: `ov-${seq}`,
    points: [...(line ?? []), ...ordered.slice(Math.max(0, nextAt)).flatMap((s) => (s.lat != null && s.lng != null ? [[s.lng, s.lat] as [number, number]] : [])), ...(me ? [[me.lng, me.lat] as [number, number]] : [])],
    padding: { top: 200, bottom: 230, left: 40, right: 40 },
  } : null;

  const due = next ? dueAt(route, next, riders) : [];
  const etaMin = dir && g ? Math.max(1, Math.round((dir.time_s * (g.remainingM / Math.max(1, cum[cum.length - 1] ?? 1))) / 60)) : null;
  const showStop = atStop || (g?.arrived ?? false);
  const onBoard = riders.filter((x) => x.status === "on" || x.status === "held").length;
  const doing = next && due[0] ? (actionAt(route, next, due[0]) === "board" ? "to pick up" : "to drop off") : "";
  const stopNo = next ? (next.site_id ? "School" : `Stop ${homeStops.findIndex((h) => h.id === next.id) + 1} of ${homeStops.length}`) : "";
  const thenTurn = g?.next && g.after && g.afterM < 300 && !showStop ? g.after : null;

  return (
    <div className="pmt-drive" role="region" aria-label={`Driving ${route.name}`}>
      <div className="pmt-drive__map">
        {/* Before the first fix: the route's stops, fitted, so the driver sees where they are going. */}
        <MapView label="Driving map" markers={markers} line={line} camera={camera} overview={overviewView} onUserMove={() => setFollow(false)} height="100%" />
      </div>

      <header className="pmt-drive__top">
        {next && showStop ? (
          <div className="pmt-drive__instruction pmt-drive__instruction--arrived"><TurnArrow type={4} /><div><div className="pmt-drive__label">Arrived</div><div className="pmt-drive__place">{next.name}</div></div></div>
        ) : next && g?.offRoute && dir ? (
          <div className="pmt-drive__instruction pmt-drive__instruction--off" aria-live="polite"><TurnArrow type={4} /><div><div className="pmt-drive__place">Rerouting…</div><div className="pmt-drive__text">Finding the way to {next.name}</div></div></div>
        ) : next && g?.next ? (
          <div className="pmt-drive__instruction" aria-live="polite">
            <div className="pmt-drive__main">
              <TurnArrow type={g.next.type} />
              <div><div className="pmt-drive__dist">{distanceLabel(g.toNextM)}</div><div className="pmt-drive__text">{g.next.street ? <b>{g.next.street}</b> : g.next.instruction}</div></div>
            </div>
            {thenTurn && <div className="pmt-drive__then"><span>Then</span><TurnArrow type={thenTurn.type} small /><span className="pmt-drive__thenwhere">{thenTurn.street ?? thenTurn.instruction}</span></div>}
          </div>
        ) : next ? (
          <div className="pmt-drive__instruction"><TurnArrow type={4} /><div><div className="pmt-drive__place">{next.name}</div><div className="pmt-drive__text">{dirError ?? (fix ? "Getting directions…" : "Waiting for GPS…")}</div></div></div>
        ) : (
          <div className="pmt-drive__instruction"><TurnArrow type={4} /><div><div className="pmt-drive__place">All done</div><div className="pmt-drive__text">Every child on this run is dealt with.</div></div></div>
        )}
        {!fix && (
          <div className="pmt-drive__gps" role="status">
            <b>{gpsError ?? "Looking for GPS"}</b>
            <span>{gpsError ? "Directions start once this phone knows where it is." : "Directions start with the first fix. Outdoors or by a window is quickest; a phone without Google services has satellites only and can take a few minutes."}</span>
          </div>
        )}
        <div className="pmt-drive__tools">
          <button type="button" className="pmt-drive__round" aria-label={muted ? "Turn the voice on" : "Mute the voice"} aria-pressed={muted} onClick={toggleMute}><Icon name={muted ? "speaker-off" : "speaker"} /></button>
          <button type="button" className="pmt-drive__round" aria-label={overview ? "Back to driving view" : "See the whole way"} aria-pressed={overview} onClick={() => (overview ? recentre() : setOverview(true))}><Icon name="route" /></button>
        </div>
      </header>

      <section className={`pmt-drive__sheet${showStop ? " pmt-drive__sheet--open" : ""}`} aria-label={next ? `Next stop: ${next.name}` : "Run done"}>
        {(!follow || overview) && me && !showStop && <button type="button" className="pmt-drive__recentre" onClick={recentre}><Icon name="locate" />Re-centre</button>}
        {!next ? (
          <div className="pmu-stack">
            <div className="pmt-drive__stopname">Run done</div>
            <p className="pmu-muted">Walk the bus and check every seat before you finish.</p>
            <Button size="lg" fullWidth onClick={onFinish}>Finish the run</Button>
          </div>
        ) : !showStop ? (
          <>
            <div className="pmt-drive__trip">
              <div className="pmt-drive__eta">
                {etaMin ? <><b>{phoneClock(Date.now() + etaMin * 60_000)}</b><span>{etaMin} min · {distanceLabel(g!.remainingM)}</span></> : <><b>—</b><span>{fix ? "Getting directions" : "Waiting for GPS"}</span></>}
              </div>
              <Button onClick={() => setAtStop(true)}>Arrived</Button>
            </div>
            <div className="pmt-drive__next">
              <span className={`pmt-drive__num${next.site_id ? " pmt-drive__num--school" : ""}`} aria-hidden="true">{next.site_id ? <Icon name="home" /> : homeStops.findIndex((h) => h.id === next.id) + 1}</span>
              <div className="pmt-drive__nextwho">
                <div className="pmt-drive__stopname">{next.name}</div>
                <div className="pmu-small pmu-muted">{stopNo} · {due.length} {doing}</div>
              </div>
            </div>
            <div className="pmt-drive__foot">
              <span className="pmt-drive__onboard"><Icon name="bus" />{onBoard} on board</span>
              <Button size="sm" variant="ghost" icon="list" onClick={onList}>Stop list</Button>
            </div>
          </>
        ) : (
          <div className="pmt-drive__atstop">
            <div className="pmt-drive__atstophead">
              <div><div className="pmt-drive__stopname">{next.name}</div><div className="pmu-small pmu-muted">{stopNo} · {due.length} {doing}</div></div>
              <Button size="sm" variant="ghost" onClick={() => setAtStop(false)}>Map</Button>
            </div>
            {due.length === 0 && <p className="pmu-muted">Nobody left here.</p>}
            <ul className="pmt-drive__kids">
              {due.map((x) => <DriveRiderRow key={x.child_id} route={route} stop={next} x={x} onAct={onAct} onHandTo={onHandTo} />)}
            </ul>
          </div>
        )}
        {/* A tablet across the dash: the whole run beside the map (shown on wide screens only). */}
        <div className="pmt-drive__all">
          <h3 className="pmt-drive__alltitle">All stops</h3>
          <StopLine route={route} stops={route.stops} riders={riders} next={next} started finished={false} onAct={onAct} onHandTo={onHandTo} />
        </div>
      </section>
    </div>
  );
}

/** One child at the stop: big buttons, one tap each. */
function DriveRiderRow<R extends DriveRiderView>({ route, stop, x, onAct, onHandTo }: {
  route: DriveRoute; stop: DriveStop; x: R; onAct: (x: R, action: Action | "absent", stop: DriveStop) => void; onHandTo: (x: R) => void;
}) {
  const act = actionAt(route, stop, x);
  return (
    <li className="pmt-drive__kid">
      <span className="pmt-drive__kidname">{fullName(x)}</span>
      <span className="pmt-drive__kidacts">
        {act === "board" && <><Button onClick={() => onAct(x, "board", stop)}>On the bus</Button><Button variant="secondary" onClick={() => onAct(x, "absent", stop)}>Not here</Button></>}
        {act === "alight" && <Button onClick={() => onAct(x, "alight", stop)}>Off the bus</Button>}
        {act === "alight-school" && <Button onClick={() => onAct(x, "alight-school", stop)}>Off at school</Button>}
        {act === "handover" && <Button onClick={() => onHandTo(x)}>Hand to an adult</Button>}
      </span>
    </li>
  );
}

